// Core imports
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Component imports
import { Row, Col, Steps } from 'antd';
import { User, Filter, EditSquare, Heart } from 'react-iconly';
import BasicInformationTab from './BasicInformationTab';
import AdvancedInformationTab from './AdvancedInformationTab';
import PersonalInformationTab from './PersonalInformationTab';
import DeathInformationTab from './DeathInformationTab';
import NdeCreateSuccessModal from './NdeCreateSuccessModal';

// Misc imports
import { NAV_NDES_CREATE, NAV_RELATIVE_NDES_ADVANCED_DETAILS, NAV_RELATIVE_NDES_BASIC_DETAILS, NAV_RELATIVE_NDES_DEATH_DETAILS, NAV_RELATIVE_NDES_PERSONAL_DETAILS, STORAGE_KEYS } from '../../constants';
import is from './../../utils/is';
import { createNde } from '../../store/ndes/ndesThunks';

// Constant declarations
const { Step } = Steps;
const NAV_BASIC_DETAILS = NAV_NDES_CREATE + NAV_RELATIVE_NDES_BASIC_DETAILS;
const NAV_DEATH_DETAILS = NAV_NDES_CREATE + NAV_RELATIVE_NDES_DEATH_DETAILS;
const NAV_ADVANCED_DETAILS = NAV_NDES_CREATE + NAV_RELATIVE_NDES_ADVANCED_DETAILS;
const NAV_PERSONAL_DETAILS = NAV_NDES_CREATE + NAV_RELATIVE_NDES_PERSONAL_DETAILS;

const NdeCreateForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [createdNdeUuid, setCreatedNdeUuid] = useState();

  // TODO: Show prompt for unsaved changes

  // On mount
  useEffect(() => {
    // If there are no saved values and the user accesses a step that is not the first one, redirect the user to the first step
    if (!isFirstStepComplete() && pathname !== NAV_BASIC_DETAILS) {
      navigate(NAV_BASIC_DETAILS, { replace: true });
    }

    // Clear the temporarily stored NDE if the user navigates to another page
    return () => {
      // TODO: Show prompt for unsaved changes
      window.localStorage.removeItem(STORAGE_KEYS.NDE_TO_CREATE);
    };
  }, []);

  // When we change steps or load the page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const isFirstStepComplete = useCallback(() => {
    const storedData = window.localStorage.getItem(STORAGE_KEYS.NDE_TO_CREATE);
    if (storedData && !is.emptyArray(JSON.parse(storedData))) {
      return true;
    } else {
      return false;
    }
  }, [window.localStorage.getItem(STORAGE_KEYS.NDE_TO_CREATE)]);

  const onSubmitSuccess = async (values, stepToNavigate) => {
    // Store saved values
    const currentValues = window.localStorage.getItem(STORAGE_KEYS.NDE_TO_CREATE) && JSON.parse(window.localStorage.getItem(STORAGE_KEYS.NDE_TO_CREATE));
    window.localStorage.setItem(STORAGE_KEYS.NDE_TO_CREATE, JSON.stringify({ ...currentValues, ...values }));

    // If there is a "next step", then navigate there
    // Otherwise, it means we're on the last step, so we submit the values and create the NDE
    if (stepToNavigate) {
      navigate(stepToNavigate);
    } else {
      const createdNde = await dispatch(createNde(JSON.parse(window.localStorage.getItem(STORAGE_KEYS.NDE_TO_CREATE))));

      if (createdNde) {
        setIsSuccessModalOpen(true);
        window.localStorage.removeItem(STORAGE_KEYS.NDE_TO_CREATE);
        setCreatedNdeUuid(createdNde.uuid);

        // Add newly created uuid to the user's IDs in local storage
        const ownNdeUuids = JSON.parse(window.localStorage.getItem(STORAGE_KEYS.OWN_NDE_UUIDS)) || [];
        ownNdeUuids.push(createdNde.uuid);
        window.localStorage.setItem(STORAGE_KEYS.OWN_NDE_UUIDS, JSON.stringify(ownNdeUuids));
      }
    }
  };

  const onStepChange = (values) => {
    // Only save current data if first step is already completed
    // and we're switching between steps, not navigating to another page
    if (isFirstStepComplete() && [NAV_BASIC_DETAILS, NAV_DEATH_DETAILS, NAV_ADVANCED_DETAILS, NAV_PERSONAL_DETAILS].includes(pathname)) {
      window.localStorage.setItem(STORAGE_KEYS.NDE_TO_CREATE, JSON.stringify(values));
    }
  };

  const getCurrentStep = () => {
    switch (pathname) {
      case NAV_BASIC_DETAILS:
      default:
        return 0;

      case NAV_DEATH_DETAILS:
        return 1;

      case NAV_ADVANCED_DETAILS:
        return 2;

      case NAV_PERSONAL_DETAILS:
        return 3;
    }
  };

  return (
    <Row gutter={[32, 32]} align="middle" justify="center" className="mt-32 mb-48">
      <Col xs={24} xl={20} xxl={18}>
        <Row gutter={[32, 32]}>
          <Col span={24} className="mb-24">
            <Steps progressDot current={getCurrentStep()}>
              <Step
                title={<Fragment><EditSquare set="curved" className="remix-icon v-align-text-bottom" /> Intro</Fragment>}
                description="(required)"
                onClick={() => navigate(NAV_BASIC_DETAILS)}
                className="cursor-pointer"
              />
              <Step
                title={<Fragment><Heart set="curved" className="remix-icon v-align-text-bottom" /> Experience</Fragment>}
                description="(optional)"
                onClick={() => isFirstStepComplete() && navigate(NAV_DEATH_DETAILS)}
                className={isFirstStepComplete() && 'cursor-pointer'}
              />
              <Step
                title={<Fragment><Filter set="curved" className="remix-icon v-align-text-bottom" /> Details</Fragment>}
                description="(optional)"
                onClick={() => isFirstStepComplete() && navigate(NAV_ADVANCED_DETAILS)}
                className={isFirstStepComplete() && 'cursor-pointer'}
              />
              <Step
                title={<Fragment><User set="curved" className="remix-icon v-align-text-bottom" /> Contact</Fragment>}
                description="(optional)"
                onClick={() => isFirstStepComplete() && navigate(NAV_PERSONAL_DETAILS)}
                className={isFirstStepComplete() && 'cursor-pointer'}
              />
            </Steps>
          </Col>

          <Col span={24}>
            <Row className="bg-color-black-0 bg-color-dark-100 border-radius elevation">
              <Col
                flex="1 1"
                className="pl-sm-16 pl-32 py-sm-24 py-32 pb-24 px-sm-24 px-32 overflow-hidden"
              >
                <Routes>
                  <Route
                    path={NAV_BASIC_DETAILS.replace(NAV_NDES_CREATE, '')}
                    exact
                    element={
                      <BasicInformationTab
                        wrapperCol={{ span: 24 }}
                        mode="create"
                        onSubmitSuccess={(values) => onSubmitSuccess(values, NAV_DEATH_DETAILS)}
                        onStepChange={(values) => onStepChange(values)}
                      />
                    }
                  />

                  <Route
                    path={NAV_DEATH_DETAILS.replace(NAV_NDES_CREATE, '')}
                    element={
                      <DeathInformationTab
                        wrapperCol={{ span: 24 }}
                        mode="create"
                        onSubmitSuccess={(values) => onSubmitSuccess(values, NAV_ADVANCED_DETAILS)}
                        onStepChange={(values) => onStepChange(values)}
                      />
                    }
                  />

                  <Route
                    path={NAV_ADVANCED_DETAILS.replace(NAV_NDES_CREATE, '')}
                    element={
                      <AdvancedInformationTab
                        wrapperCol={{ span: 24 }}
                        mode="create"
                        onSubmitSuccess={(values) => onSubmitSuccess(values, NAV_PERSONAL_DETAILS)}
                        onStepChange={(values) => onStepChange(values)}
                      />
                    }
                  />

                  <Route
                    path={NAV_PERSONAL_DETAILS.replace(NAV_NDES_CREATE, '')}
                    element={
                      <PersonalInformationTab
                        wrapperCol={{ span: 24 }}
                        mode="create"
                        onSubmitSuccess={(values) => onSubmitSuccess(values)} // Final submit
                        onStepChange={(values) => onStepChange(values)}
                      />
                    }
                  />
                </Routes>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>

      <NdeCreateSuccessModal visible={isSuccessModalOpen} setVisible={setIsSuccessModalOpen} ndeUuid={createdNdeUuid} />
    </Row>
  );
};

export default NdeCreateForm;
